import { useState, useCallback } from "react";
import { useCreate, useGetList, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { X, Plus, Loader2, ClipboardList } from "lucide-react";

interface Job {
  id: string;
  job_number: string;
  title: string;
}

interface CreatePODialogProps {
  open: boolean;
  onClose: () => void;
  vendorId: string;
  onCreated?: (id: string) => void;
}

export function CreatePODialog({ open, onClose, vendorId, onCreated }: CreatePODialogProps) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [creating, setCreating] = useState(false);

  const { data: jobs } = useGetList<Job>("jobs", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });

  const [form, setForm] = useState({
    job_id: "",
    description: "",
    total: 0,
    expected_by: "",
  });

  const handleCreate = useCallback(async () => {
    if (form.total <= 0) {
      notify("Total must be greater than 0", { type: "warning" });
      return;
    }
    setCreating(true);
    try {
      const result = await create(
        "purchase_orders",
        {
          data: {
            vendor_id: vendorId,
            job_id: form.job_id || null,
            description: form.description.trim(),
            subtotal: form.total,
            total: form.total,
            expected_by: form.expected_by || null,
            line_items_json: [
              { description: form.description.trim() || "Purchase", quantity: 1, unit_price: form.total, amount: form.total },
            ],
          },
        },
        { returnPromise: true }
      );

      await create("events", {
        data: {
          job_id: form.job_id || null,
          event_type: "po_created",
          payload: {
            vendor_id: vendorId,
            po_id: (result as { data?: { id?: string } })?.data?.id,
            total: form.total,
            description: form.description.trim(),
          },
        },
      }, { returnPromise: true });

      notify("Purchase order created", { type: "success" });
      refresh();
      const newId = (result as { data?: { id?: string } })?.data?.id;
      if (newId && onCreated) onCreated(newId);
      onClose();
      setForm({ job_id: "", description: "", total: 0, expected_by: "" });
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setCreating(false);
    }
  }, [form, vendorId, create, notify, refresh, onCreated, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <ClipboardList className="w-4 h-4" /> New Purchase Order
            </h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Link to job</label>
              <select value={form.job_id}
                onChange={(e) => setForm((f) => ({ ...f, job_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm">
                <option value="">— No job —</option>
                {(jobs || []).map((j) => (
                  <option key={j.id} value={j.id}>{j.job_number} — {j.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Description</label>
              <input type="text" value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Lumber for framing"
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Total ($)</label>
                <input type="number" min={0} step={0.01} value={form.total}
                  onChange={(e) => setForm((f) => ({ ...f, total: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded text-sm text-right" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Expected by</label>
                <input type="date" value={form.expected_by}
                  onChange={(e) => setForm((f) => ({ ...f, expected_by: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button onClick={onClose} disabled={creating}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={handleCreate} disabled={creating || form.total <= 0}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create PO
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
